import { Card, CardContent, TableCell, Typography } from "@mui/material";
import type { z } from "zod";
import { omitNullFields } from "../../../components/Form";
import Link from "../../../components/Link";
import type { Column } from "../../../components/Table";
import {
  dateTimeColumns,
  getActiveFiltersCount,
  makeRequestSchema,
  prepareListRequest,
  ResourceTable,
  useSearchRequest,
  withoutBaseTableModel,
} from "../../../components/Table";
import { useGroups } from "../../../domain/crud";
import { makeGroupLocation, makeNewGroupLocation } from "../../../paths";
import type { Group } from "../../../services/datastore";
import { GroupFilters } from "./GroupFilters";
import { listGroupsSchema } from "./validation";

const COLUMNS: ReadonlyArray<Column<Group>> = [
  {
    header: "Group",
    sortKey: "name",
    renderCell(group) {
      return (
        <TableCell>
          {group.name}
          <Link to={makeGroupLocation(group.id)}>
            <Typography variant="body2">{group.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  ...dateTimeColumns,
];

const requestSchema = makeRequestSchema(COLUMNS, listGroupsSchema);

export default function Table() {
  const [request, setRequest] = useSearchRequest(requestSchema);
  const searchQuery = useGroupSearch(request);

  const filterValues = withoutBaseTableModel(request);

  return (
    <Card>
      <CardContent>
        <ResourceTable
          resourceName="group"
          resourceCreateLocation={makeNewGroupLocation()}
          getRowKey={(group) => group.id}
          columns={COLUMNS}
          searchQuery={searchQuery}
          tableModel={request}
          onTableModelChange={setRequest}
          filterSection={
            <GroupFilters values={filterValues} setValues={setRequest} />
          }
          activeFilterCount={getActiveFiltersCount(filterValues)}
        />
      </CardContent>
    </Card>
  );
}

function useGroupSearch(request: z.infer<typeof requestSchema>) {
  return useGroups(omitNullFields(prepareListRequest(request)), {
    keepPreviousData: true,
    cacheTime: 0,
  });
}
